import OrderActivity from './components/compartidos/OrderActivity';
import UnsavedChangesHost, { guardAdminNavigation } from './components/admin/UnsavedChanges';
import StandaloneRoleScreen from './components/admin/StandaloneRoleScreen';
import LawyerWorkspace from './components/admin/LawyerWorkspace';
// src/App.js
import React, { useState, useEffect, useLayoutEffect, useCallback, useMemo, useRef } from 'react';
import './styles/App.css';
import './components/compartidos/GlobalMobile.css';

import Header from './components/compartidos/Header';
import Footer from './components/compartidos/Footer';
import AuthSection from './components/cuenta/AuthSection';
import HomePage from './components/landing/HomePage';
import NosotrosPage from './components/landing/NosotrosPage';
import { clearStoredPowerDrafts } from './utils/powerDraftStorage';
import LegalPage from './components/landing/LegalPage';
import FloatingHelp from './components/compartidos/FloatingHelp';

// --- IMPORTACIONES DE STRIPE ---
import { loadStripe } from '@stripe/stripe-js';
import { Elements } from '@stripe/react-stripe-js';

// --- TRADUCCIONES (idioma fijo: español) ---
import { translations } from './data/translations';

// Separar funcionalidades pesadas del bundle de la landing sin modificar su UI.
// Mantener loaders estables permite precargar durante los 400 ms de transición.
const sectionModuleLoaders = {
  'crear-accion': () => import('./components/acciones/AccionesSection'),
  'crear-poder': () => import('./components/poderes/PoderesSection'),
  profile: () => import('./components/cuenta/ProfileSection'),
  facturacion: () => import('./components/cuenta/FacturacionPage'),
  admin: () => import('./components/admin/AdminSection'),
  'notario-dashboard': () => import('./components/admin/NotarioDashboard'),
};

const AccionesSection = React.lazy(sectionModuleLoaders['crear-accion']);
const PoderesSection = React.lazy(sectionModuleLoaders['crear-poder']);
const ProfileSection = React.lazy(sectionModuleLoaders.profile);
const FacturacionPage = React.lazy(sectionModuleLoaders.facturacion);
const AdminSection = React.lazy(sectionModuleLoaders.admin);
const NotarioDashboard = React.lazy(sectionModuleLoaders['notario-dashboard']);

// ============================================
// FIX #1: Stripe key desde variable de entorno
// ============================================
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

if (!STRIPE_PUBLISHABLE_KEY) {
  // eslint-disable-next-line no-console
  console.error(
    'REACT_APP_STRIPE_PUBLISHABLE_KEY no está configurada. Los pagos no funcionarán.'
  );
}

// FIX #1: Advertir si se usa key de test en producción
if (
  process.env.NODE_ENV === 'production' &&
  STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_')
) {
  // eslint-disable-next-line no-console
  console.error(
    '⚠️ ADVERTENCIA: Usando Stripe TEST key en producción. Los pagos NO serán reales.'
  );
}

const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY)
  : null;

const elementsOptions = {
  locale: 'es',
  appearance: {
    theme: 'stripe',
    variables: {
      fontFamily:
        "Manrope, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
      colorText: '#1A2E44',
    },
    rules: {
      '.Input': {
        fontSize: '16px',
      },
    },
  },
};

// ============================================
// CONSTANTES DE SEGURIDAD
// ============================================

// FIX #10: Whitelist de secciones válidas
const VALID_SECTIONS = new Set([
  'inicio',
  'nosotros',
  'registrate',
  'auth',
  'crear-accion',
  'crear-poder',
  'profile',
  'facturacion',
  'admin',
  'folios-abogado',
  'notario-dashboard',
  'legal-terms',
  'legal-privacy',
]);

// FIX #7, #15: API URL con validación
const API_URL = (() => {
  const url = process.env.REACT_APP_API_URL || '';

  if (!url) {
    if (process.env.NODE_ENV === 'production') {
      // eslint-disable-next-line no-console
      console.error('REACT_APP_API_URL no configurada en producción');
      return ''; // Forzar que las peticiones fallen en lugar de ir a localhost
    }
    return 'http://localhost:5001';
  }

  // FIX #15: En producción, verificar HTTPS
  if (process.env.NODE_ENV === 'production' && !url.startsWith('https://')) {
    // eslint-disable-next-line no-console
    console.error('API_URL debe usar HTTPS en producción');
  }

  // Quitar trailing slash
  return url.replace(/\/+$/, '');
})();

const ENV_DEBUG_STATUS = {
  apiUrl: API_URL ? 'PRESENT' : 'MISSING',
  googleMapsKey: process.env.REACT_APP_GOOGLE_MAPS_KEY ? 'PRESENT' : 'MISSING',
  stripeKey: STRIPE_PUBLISHABLE_KEY ? 'PRESENT' : 'MISSING',
};

const ENV_DEBUG_ENABLED =
  process.env.NODE_ENV !== 'production' ||
  process.env.REACT_APP_SHOW_ENV_STATUS === 'true';

if (ENV_DEBUG_ENABLED) {
  // eslint-disable-next-line no-console
  console.log('ENV BUILD STATUS:', ENV_DEBUG_STATUS, 'API_URL=', API_URL);
}

// Secciones donde NO se muestra el footer
const FOOTER_HIDDEN_SECTIONS = new Set([
  'inicio',
  'registrate',
  'auth',
  'crear-accion',
  'crear-poder',
  'profile',
  'facturacion',
  'admin',
  'folios-abogado',
  'notario-dashboard',
  'legal-terms',
  'legal-privacy',
]);

// ============================================
// UTILIDADES DE SEGURIDAD
// ============================================

/**
 * FIX #5: Sanitizar string de localStorage para prevenir XSS
 */
function sanitizeStoredString(value, maxLength = 200) {
  if (!value || typeof value !== 'string') return '';
  return value
    .replace(/[<>"'&]/g, '')
    .split('')
    .filter((char) => {
      const code = char.charCodeAt(0);
      return code > 31 && code !== 127;
    })
    .join('')
    .trim()
    .substring(0, maxLength);
}

/**
 * FIX #4: Validar formato básico de JWT
 * (No verifica la firma — eso lo hace el servidor)
 */
function isValidTokenFormat(token) {
  if (!token || typeof token !== 'string') return false;
  if (token === 'undefined' || token === 'null') return false;

  // JWT tiene 3 partes separadas por puntos
  const parts = token.split('.');
  if (parts.length !== 3) return false;

  // Cada parte debe ser base64url válido
  const base64urlRegex = /^[A-Za-z0-9_-]+$/;
  return parts.every((part) => part.length > 0 && base64urlRegex.test(part));
}

/**
 * FIX #13: Verificar si el JWT ha expirado (client-side)
 */
function isTokenExpired(token) {
  if (!isValidTokenFormat(token)) return true;

  try {
    const parts = token.split('.');
    // Decodificar payload (parte 2)
    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));

    if (!payload.exp) return false; // Sin expiración (depender del servidor)

    // Expirado si la fecha actual es mayor que exp (con 30s de buffer)
    const now = Math.floor(Date.now() / 1000);
    return now >= payload.exp - 30;
  } catch {
    return true; // Si no se puede decodificar, considerarlo expirado
  }
}

/**
 * FIX #11: Validar rol contra whitelist
 */
function validateRole(role) {
  const validRoles = ['user', 'admin', 'notario', 'admin_notaria', 'abogado_wisby', 'abogado_notaria'];
  if (validRoles.includes(role)) return role;
  return 'user';
}
function homeForRole(role) {
  if (role === 'admin') return 'admin';
  if (role === 'abogado_wisby') return 'folios-abogado';
  if (['notario', 'admin_notaria', 'abogado_notaria'].includes(role)) return 'notario-dashboard';
  return 'inicio';
}

/**
 * FIX #6: Logger seguro — solo en desarrollo
 */
const secureLog = {
  info: (...args) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.log(...args);
    }
  },
  warn: (...args) => {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(...args);
    }
  },
  error: (...args) => {
    // Errores siempre se loguean pero sin datos sensibles
    // eslint-disable-next-line no-console
    console.error(...args);
  },
};

// ============================================
// HELPER: Extraer array de boletas de cualquier respuesta
// ============================================
const extractBoletasArray = (input) => {
  if (Array.isArray(input)) return input;
  if (input && Array.isArray(input.boletas)) return input.boletas;
  if (input && Array.isArray(input.data)) return input.data;
  return [];
};

// ============================================
// FIX #3, #8, #14: Detectar reset password e invite notario params de forma segura
// ============================================

// Helper simple y directo: detecta si la URL actual es una vista de auth especial.
// Retorna 'resetPassword', 'invite' o null.
const getAuthViewFromURL = () => {
  try {
    const search = window.location.search || '';
    const params = new URLSearchParams(search);
    const view = params.get('view');
    const token = params.get('token');
    if (token && token.length >= 16 && (view === 'resetPassword' || view === 'invite')) {
      return view;
    }
  } catch {
    secureLog.warn('Error parseando params de URL');
  }
  return null;
};

// Mantener compatibilidad con el efecto de limpieza de sesión (resetPassword)
const getInitialResetParams = () => {
  const view = getAuthViewFromURL();
  return {
    isReset: view === 'resetPassword',
    isInvite: view === 'invite',
    token: null,
  };
};

// ============================================
// SECURE STORAGE HELPERS
// Almacenamiento dual: localStorage (recordar sesión) o sessionStorage (solo pestaña)
// ============================================
const SESSION_KEYS = ['token', 'userName', 'userEmail', 'userRole'];

// La preferencia "recordar sesión" se guarda siempre en localStorage
const readRememberMeFlag = () => {
  const flag = localStorage.getItem('rememberMe');
  if (flag === 'true') return true;
  if (flag === 'false') return false;
  // Compatibilidad: sesiones previas sin flag → token en localStorage = recordado
  return !!localStorage.getItem('token');
};

const secureStorage = {
  setSession: (token, userName, userEmail, userRole, rememberMe) => {
    if (!isValidTokenFormat(token)) {
      secureLog.error('Intento de guardar token con formato inválido');
      return false;
    }
    // Limpiar datos de sesión previos en ambos almacenamientos
    SESSION_KEYS.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    const wantsRemember = rememberMe === true;
    localStorage.setItem('rememberMe', wantsRemember ? 'true' : 'false');
    const storage = wantsRemember ? localStorage : sessionStorage;
    storage.setItem('token', token);
    storage.setItem('userName', sanitizeStoredString(userName, 100));
    storage.setItem('userEmail', sanitizeStoredString(userEmail, 254));
    storage.setItem('userRole', validateRole(userRole));
    return true;
  },

  // Almacenamiento activo según la preferencia de "recordar sesión"
  _store: () => (readRememberMeFlag() ? localStorage : sessionStorage),

  getToken: () => {
    const token = secureStorage._store().getItem('token');
    if (!isValidTokenFormat(token)) return null;
    if (isTokenExpired(token)) {
      secureLog.info('Token expirado, limpiando sesión');
      secureStorage.clearSession();
      return null;
    }
    return token;
  },

  // Token sin validar expiración (para la lógica de renovación)
  getRawToken: () => {
    const token = secureStorage._store().getItem('token');
    if (!isValidTokenFormat(token)) return null;
    return token;
  },

  // Segundos hasta la expiración del token (null si no hay token o sin claim exp)
  getTimeToExpiry: () => {
    const token = secureStorage.getRawToken();
    if (!token) return null;
    try {
      const parts = token.split('.');
      const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
      if (!payload.exp) return null;
      return payload.exp - Math.floor(Date.now() / 1000);
    } catch {
      return null;
    }
  },

  // Actualizar solo el token (renovación silenciosa)
  updateToken: (newToken) => {
    if (!isValidTokenFormat(newToken)) {
      secureLog.error('Token de renovación con formato inválido');
      return false;
    }
    secureStorage._store().setItem('token', newToken);
    return true;
  },

  getUserName: () => sanitizeStoredString(secureStorage._store().getItem('userName'), 100),
  getUserEmail: () => sanitizeStoredString(secureStorage._store().getItem('userEmail'), 254),
  getUserRole: () => validateRole(secureStorage._store().getItem('userRole')),
  setUserRole: (role) => secureStorage._store().setItem('userRole', validateRole(role)),
  isRemembered: () => readRememberMeFlag(),

  clearSession: () => {
    SESSION_KEYS.forEach((k) => {
      localStorage.removeItem(k);
      sessionStorage.removeItem(k);
    });
    localStorage.removeItem('rememberMe');
  },

  /**
   * Emitir evento de logout para sincronizar entre pestañas
   */
  broadcastLogout: () => {
    localStorage.setItem('logout_event', Date.now().toString());
    localStorage.removeItem('logout_event');
  },
};

// ============================================
// COMPONENTE PRINCIPAL
// ============================================
export default function App() {
  // Capturar params de reset ANTES de todo
  const [resetParams] = useState(getInitialResetParams);

  // Detección DIRECTA de vistas de auth especial en la URL inicial.
  // Si la URL tiene ?view=invite&token=... o ?view=resetPassword&token=...
  // arrancamos directamente en 'auth' para montar AuthSection.
  const [activeSection, setActiveSection] = useState(() => {
    const authView = getAuthViewFromURL();
    if (authView) {
      secureLog.info('URL de auth especial detectada en carga inicial:', authView);
      return 'auth';
    }
    if (resetParams.isReset || resetParams.isInvite) return 'auth';
    return 'inicio';
  });

  // RED DE SEGURIDAD: si por cualquier motivo la sección inicial no llegó a
  // ser 'auth' pero la URL sigue teniendo params de invite/reset, forzarla
  // ANTES del paint del navegador (useLayoutEffect) para que el usuario
  // nunca vea ni un destello de la landing page.
  useLayoutEffect(() => {
    const authView = getAuthViewFromURL();
    if (authView) {
      secureLog.info('Safety net: forzando sección auth para view:', authView);
      setActiveSection(prev => prev === 'auth' ? prev : 'auth');
    }
    // Solo en montaje
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [isExiting, setIsExiting] = useState(false);
  const [boletasData, setBoletasData] = useState([]);
  const [poderesData, setPoderesData] = useState([]);
  const [profileTab, setProfileTab] = useState('boletas');

  // Idioma fijo: español. Si en el futuro quieres reactivar el selector
  // de idiomas, basta con convertir esto en useState.
  const t = translations.esp;

  // FIX #11: Verificar isAdmin con validación
  const isAdmin = userRole === 'admin';

  // FIX #3: Si viene de reset → forzar logout y limpiar
  // ============================================
  useEffect(() => {
    if (resetParams.isReset) {
      secureStorage.clearSession();
      setCurrentUser(null);
      setUserRole(null);
      setBoletasData([]);
    }
  }, [resetParams.isReset]);

  // ============================================
  // Logout
  // ============================================
  const handleLogout = useCallback(() => guardAdminNavigation(() => {
    clearStoredPowerDrafts();
    setIsExiting(true);
    setTimeout(() => {
      secureStorage.clearSession();
      secureStorage.broadcastLogout();

      setCurrentUser(null);
      setUserRole(null);
      setBoletasData([]);
      setActiveSection('inicio');
      setIsExiting(false);
      window.scrollTo(0, 0);
    }, 400);
  }), []);

  // ============================================
  // Fetch Boletas
  // ============================================
  const fetchBoletas = useCallback(
    async (manualToken, options = {}) => {
      const { suppressLogout = false } = options;
      const token = manualToken || secureStorage.getToken();
      if (!token) return;

      if (!isValidTokenFormat(token)) {
        if (!suppressLogout) handleLogout();
        return;
      }

      if (isTokenExpired(token)) {
        if (!suppressLogout) handleLogout();
        return;
      }

      if (!API_URL) return;

      try {
        const response = await fetch(`${API_URL}/api/boletas`, {
          method: 'GET',
          headers: {
            'x-auth-token': token,
            'Content-Type': 'application/json',
          },
        });

        if (response.status === 401) {
          secureLog.info('Sesión expirada (401 del servidor)');
          if (!suppressLogout) handleLogout();
          return;
        }

        if (response.ok) {
          const json = await response.json();
          setBoletasData(extractBoletasArray(json));
        }
      } catch (error) {
        secureLog.error('Error de red al cargar boletas');
      }
    },
    [handleLogout]
  );

  // ============================================
  // Renovación silenciosa del token (sesión persistente)
  // ============================================
  const lastRefreshRef = useRef(0);

  const refreshToken = useCallback(async ({ force = false } = {}) => {
    if (!API_URL) return false;

    const ttl = secureStorage.getTimeToExpiry();
    // Sin token o sin claim exp → no hay nada que renovar
    if (ttl === null) return false;
    // Token ya expirado → el backend rechazará la renovación
    if (ttl <= 0) return false;
    // Renovación periódica: solo si está próximo a expirar (<= 1 hora)
    if (!force && ttl > 3600) return true;
    // Throttle: no renovar más de una vez por minuto
    const now = Date.now();
    if (now - lastRefreshRef.current < 60 * 1000) return true;

    const token = secureStorage.getRawToken();
    if (!token) return false;

    try {
      const res = await fetch(`${API_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-auth-token': token,
        },
      });
      if (!res.ok) {
        secureLog.warn('Renovación de token fallida', { status: res.status });
        return false;
      }
      const data = await res.json();
      if (!data.token || !isValidTokenFormat(data.token)) return false;

      secureStorage.updateToken(data.token);
      lastRefreshRef.current = Date.now();
      secureLog.info('Token renovado correctamente');
      return true;
    } catch {
      secureLog.warn('Error de red al renovar token');
      return false;
    }
  }, []);

  // Ref para activeSection (evita stale closures en changeSection)
  const activeSectionRef = useRef(activeSection);

  useEffect(() => {
    activeSectionRef.current = activeSection;
  }, [activeSection]);

  const changeSection = useCallback(
    (section, profileTabName) => guardAdminNavigation(() => {
      const role = secureStorage.getUserRole();
      if (['admin', 'abogado_wisby', 'notario', 'admin_notaria', 'abogado_notaria'].includes(role)
          && ['crear-poder', 'crear-accion', 'profile', 'facturacion'].includes(section)) return;
      if ((section === 'admin' && role !== 'admin')
        || (section === 'folios-abogado' && role !== 'abogado_wisby')
        || (section === 'notario-dashboard' && !['notario', 'admin_notaria', 'abogado_notaria'].includes(role))) return;
      if (!VALID_SECTIONS.has(section)) {
        secureLog.warn('Sección inválida solicitada:', section);
        return;
      }
      if (profileTabName) {
        setProfileTab(profileTabName);
      }
      if (section === activeSectionRef.current) {
        if (profileTabName) {
          window.dispatchEvent(new CustomEvent('forceProfileTabChange', { detail: profileTabName }));
        }
        return;
      }
      // Iniciar la descarga antes de la transición existente, sin bloquearla.
      // React.lazy reutiliza el módulo importado por webpack cuando se renderiza.
      const loadSection = sectionModuleLoaders[section];
      if (loadSection) loadSection().catch(() => {});
      if (activeSectionRef.current === 'crear-poder') {
        // Salir del módulo siempre inicia una captura nueva la próxima vez.
        // Esto también cubre la navegación desde el header y el menú de perfil.
        clearStoredPowerDrafts();
      }
      setIsExiting(true);
      setTimeout(() => {
        setActiveSection(section);
        activeSectionRef.current = section;
        setIsExiting(false);
        window.scrollTo(0, 0);
      }, 400);
    }),
    []
  );

  // Backend validates the role before this callback. Clear role-specific UI
  // state immediately to avoid showing a previous workspace or its data.
  const handleActiveRoleChanged = useCallback((newRole) => {
    if (!secureStorage.getToken()) return;
    const role = validateRole(newRole);
    secureStorage.setUserRole(role);
    setUserRole(role);
    setActiveSection(homeForRole(role));
    activeSectionRef.current = homeForRole(role);
    setIsExiting(false);
    setProfileTab('boletas');
    setBoletasData([]);
    setPoderesData([]);
    window.scrollTo(0, 0);
    if (role === 'user') fetchBoletas(secureStorage.getToken(), { suppressLogout: true });
  }, [fetchBoletas]);

  const handleLogin = useCallback(
    (userName, token, userEmail, userRoleFromServer, rememberMe) => {
      if (!isValidTokenFormat(token)) {
        secureLog.error('Token inválido recibido en login');
        return;
      }

      const saved = secureStorage.setSession(
        token, userName, userEmail, userRoleFromServer, rememberMe
      );
      if (!saved) {
        secureLog.error('Error guardando sesión');
        return;
      }

      setCurrentUser(sanitizeStoredString(userName, 100));
      setUserRole(validateRole(userRoleFromServer));
      // El equipo operativo entra directamente a sus folios.
      const role = validateRole(userRoleFromServer);
      changeSection(homeForRole(role));
      secureLog.info('Login exitoso');
      fetchBoletas(token, { suppressLogout: true });
    },
    [fetchBoletas, changeSection]
  );

  // ============================================
  // Recuperar sesión (SOLO si NO viene de reset ni de invitación de notario)
  // ============================================
  useEffect(() => {
    if (resetParams.isReset || resetParams.isInvite) return;

    const user = secureStorage.getUserName();
    const token = secureStorage.getToken();
    const role = secureStorage.getUserRole();

    if (user && token) {
      setCurrentUser(user);
      setUserRole(role);
      fetchBoletas(token);
      const staffSection = homeForRole(role);
      setActiveSection(staffSection);
      activeSectionRef.current = staffSection;
      // Extender la sesión en cada visita (renovación forzada al cargar)
      refreshToken({ force: true });
    }
  }, [fetchBoletas, resetParams.isReset, resetParams.isInvite, refreshToken]);

  // ============================================
  // Escuchar cambios de localStorage desde OTRAS pestañas
  // ============================================
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'logout_event') {
        setCurrentUser(null);
        setUserRole(null);
        setBoletasData([]);
        setActiveSection('inicio');
        return;
      }

      if (event.key === 'token') {
        if (event.newValue && isValidTokenFormat(event.newValue)) {
          const user = secureStorage.getUserName();
          const role = secureStorage.getUserRole();
          setCurrentUser(user);
          setUserRole(role);
          fetchBoletas(event.newValue);
        } else {
          setCurrentUser(null);
          setUserRole(null);
          setBoletasData([]);
          setActiveSection('inicio');
        }
      }

      if (event.key === 'userName' && event.newValue) {
        setCurrentUser(sanitizeStoredString(event.newValue, 100));
      }
      if (event.key === 'userRole' && event.newValue) {
        // El rol efectivo es global a la cuenta en la BD: otras pestañas deben
        // abandonar su pantalla anterior, respetando cambios no guardados.
        const role = validateRole(event.newValue);
        guardAdminNavigation(() => handleActiveRoleChanged(role));
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchBoletas, handleActiveRoleChanged]);

  // ============================================
  // Renovación periódica: mantener la sesión activa
  // (renueva el token antes de que expire en lugar de cerrar sesión)
  // ============================================
  useEffect(() => {
    if (!currentUser) return;

    const checkAndRefresh = async () => {
      const ttl = secureStorage.getTimeToExpiry();
      // Si el token sigue vigente y lejos de expirar, no hacer nada
      if (ttl !== null && ttl > 3600) return;

      const refreshed = await refreshToken();
      // Si no se pudo renovar y el token ya expiró → cerrar sesión
      if (!refreshed) {
        const remaining = secureStorage.getTimeToExpiry();
        if (remaining !== null && remaining <= 0) {
          secureLog.info('Sesión expirada sin poder renovar');
          handleLogout();
        }
      }
    };

    // Verificar al montar y cada 5 minutos
    checkAndRefresh();
    const interval = setInterval(checkAndRefresh, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [currentUser, refreshToken, handleLogout]);

  const handleAddBoleta = useCallback((newBoleta) => {
    if (!newBoleta || typeof newBoleta !== 'object') return;
    setBoletasData((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return [newBoleta, ...prevArray];
    });
  }, []);

  const handleAddPoder = useCallback((newPoder) => {
    if (!newPoder || typeof newPoder !== 'object') return;
    setPoderesData((prev) => {
      const prevArray = Array.isArray(prev) ? prev : [];
      return [newPoder, ...prevArray];
    });
  }, []);

  // Memoizar para evitar re-renders innecesarios
  const showFooter = useMemo(
    () => !FOOTER_HIDDEN_SECTIONS.has(activeSection),
    [activeSection]
  );

  // Guard por si las traducciones no cargan
  if (!t) return <div>Cargando...</div>;

  // ============================================
  // RENDER
  // ============================================
  const roleScreenUserId = new URLSearchParams(window.location.search).get('roleScreen');
  const isWorkspace = Boolean(currentUser) && ['notario-dashboard', 'folios-abogado'].includes(activeSection);
  const content = roleScreenUserId ? <StandaloneRoleScreen userId={roleScreenUserId} /> : (
    <div className={`App${isWorkspace ? ' App--workspace' : ''}`}>

      <Header
        currentUser={currentUser}
        userRole={userRole}
        isAdmin={isAdmin}
        onSetActiveSection={changeSection}
        onLogout={handleLogout}
        onRoleChanged={handleActiveRoleChanged}
        onSetProfileTab={setProfileTab}
        isLanding={['inicio', 'auth', 'registrate'].includes(activeSection)}
        isWorkspace={isWorkspace}
        t={t.header}
        notifications={<OrderActivity key={userRole || 'guest'} currentUser={currentUser} userRole={userRole} onLogin={handleLogin} inHeader />}
      />

      <main className={`main-content ${isExiting ? 'exiting' : ''}`}>
        <React.Suspense fallback={<div className="staff-loading-state" role="status">Cargando espacio de trabajo…</div>}>
        {activeSection === 'inicio' && (
          <HomePage
            currentUser={currentUser}
            onNavigate={changeSection}
            t={t}
          />
        )}

        {activeSection === 'folios-abogado' && currentUser && userRole === 'abogado_wisby' && <LawyerWorkspace />}
        {currentUser && ['notario', 'admin_notaria', 'abogado_notaria'].includes(userRole) && activeSection !== 'notario-dashboard' && <button onClick={() => changeSection('notario-dashboard')}>Mis expedientes</button>}
        {activeSection === 'nosotros' && (
          <NosotrosPage t={t.nosotrosPage} />
        )}

        {(activeSection === 'registrate' || activeSection === 'auth') && (
          <AuthSection
            onLogin={handleLogin}
            onNavigate={changeSection}
            initialView={activeSection === 'registrate' ? 'register' : 'login'}
            t={t.auth}
          />
        )}

        {activeSection === 'crear-accion' &&
          (currentUser ? (
            <AccionesSection
              currentUser={currentUser}
              onSendBoleta={handleAddBoleta}
              onFinalize={changeSection}
              t={t.acciones}
            />
          ) : (
            <AuthSection
              onLogin={handleLogin}
              onNavigate={changeSection}
              t={t.auth}
            />
          ))}

        {activeSection === 'crear-poder' &&
          (currentUser ? (
            <PoderesSection
              currentUser={currentUser}
              onFinalize={changeSection}
              onPoderCreated={handleAddPoder}
            />
          ) : (
            <AuthSection
              onLogin={handleLogin}
              onNavigate={changeSection}
              t={t.auth}
            />
          ))}

        {activeSection === 'profile' && currentUser && userRole === 'user' && (
          <ProfileSection
            boletasData={boletasData}
            poderesData={poderesData}
            onLogout={handleLogout}
            onNavigate={changeSection}
            initialTab={profileTab}
            isAdmin={isAdmin}
            t={t.profile}
          />
        )}

        {activeSection === 'facturacion' && userRole === 'user' &&
          (currentUser ? (
            <FacturacionPage
              boletasData={boletasData}
              poderesData={poderesData}
              onNavigate={changeSection}
              currentUser={currentUser}
            />
          ) : (
            <AuthSection
              onLogin={handleLogin}
              onNavigate={changeSection}
              t={t.auth}
            />
          ))}

        {activeSection === 'admin' && currentUser && isAdmin && (
          <AdminSection
            onNavigateBack={() => changeSection('profile')}
            onNavigateHome={() => changeSection('inicio')}
          />
        )}

        {activeSection === 'notario-dashboard' && currentUser && ['notario', 'admin_notaria', 'abogado_notaria'].includes(userRole) && (
          <NotarioDashboard
            key={userRole}
            currentUser={currentUser}
            userRole={userRole}
            onLogout={handleLogout}
            onNavigate={changeSection}
          />
        )}

        {activeSection === 'legal-terms' && (
          <LegalPage
            onNavigate={changeSection}
            initialTab="terms"
            t={t.legal}
          />
        )}

        {activeSection === 'legal-privacy' && (
          <LegalPage
            onNavigate={changeSection}
            initialTab="privacy"
            t={t.legal}
          />
        )}
        </React.Suspense>
      </main>

      {showFooter && <Footer t={t.footer} />}
      
      {/* 
        El circulito flotante de ayuda se muestra en todas las secciones
        EXCEPTO cuando la sección activa sea "admin".
      */}
      {!['admin', 'folios-abogado', 'notario-dashboard'].includes(activeSection) && <FloatingHelp />}
    </div>
  );

  // Envolver con Elements solo si Stripe está configurado
  if (stripePromise) {
    return (
      <Elements stripe={stripePromise} options={elementsOptions}>
        <UnsavedChangesHost />{content}
      </Elements>
    );
  }

  return <><UnsavedChangesHost />{content}</>;
}
